import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import * as Cesium from "cesium";
import useUrlParams from "./useUrlParams";
import {
    create_gs_packet,
    createSatellitePackets,
    updateGsContactRings,
} from "./cesiumUtils";
import useViewer from "./useViewer";
import useGroundContact from "./useGroundContact";
import useOrbit from "./useOrbit";
import { getMapStyleId } from "../../../utils/cesiumUtil";
import useLoader from "./useLoader";
import { createNaturalEarthProvider, offline, setup_gs_hover } from "../../../utils/commonFunctions";
import { SatelliteGroupOrbitType, VisualizationDataType } from "../types";
import useSatellite from "./useSatellite";
import MissionService from "../../../service/MissionService";
import useScenario from "./useScenario";
import { isOperateMode } from "./utils";
import { SCENARIO_DEPLOYMENT_TYPES } from "../../simulate/constants";
import useContacts from "./useContacts";


const useCesium = () => {
    const { setLoading } = useLoader()
    const { configured_gs } = useGroundContact()
    const cesiumContainerRef = useRef<HTMLDivElement | null>(null)
    const gsHoverHandlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null)
    const { scenario_id, dashboard, sat_grp_id, sat_id, mission_id } = useUrlParams()
    const { selected_satellite_group } = useSatellite()
    const { scenario } = useScenario()
    const { set_viewer, update_viewer } = useViewer()
    const { orbitData } = useOrbit()
    const {contacts} = useContacts()
    const {ongoing_contacts} = contacts
    const gsStatusList = useSelector((state: any) => state.mission.gsStatusList)
    const [map_viewer, set_map_viewer] = useState<Cesium.Viewer | null>(null)
    const [groundTargets, setGroundTargets] = useState<any[]>([])
    const inertiaZoomValue = 40000000;
    const maximumZoomDistanceValue = 35500000;
    const maximumZoomDistValue3D = 60000000;

    const Imaginary = new Cesium.MapboxStyleImageryProvider({
        styleId: 'dark-v10',
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN!
    });

    const update_base_map_layer = (map_viewer, baseMapOption) => {
        const imaginary = new Cesium.MapboxStyleImageryProvider({
            styleId: getMapStyleId(baseMapOption),
            accessToken: process.env.REACT_APP_MAPBOX_TOKEN!
        });
        map_viewer.imageryLayers.addImageryProvider(offline() ? createNaturalEarthProvider() : imaginary);
    }


    useEffect(() => {
        if (cesiumContainerRef.current && scenario_id) {
            const cesium_viewer = new Cesium.Viewer(cesiumContainerRef.current, {
                requestRenderMode: true,
                shouldAnimate: true,
                animation: false, // Disable the default animation widget
                baseLayerPicker: false, // Disable the default base layer picker
                timeline: false, // Disable the timeline
                geocoder: false, // Disable the geocoder
                homeButton: false, // Disable the home button
                navigationHelpButton: false, // Disable the navigation help button
                sceneModePicker: false, // Disable the scene mode picker
                selectionIndicator: false, // Disable the selection indicator
                fullscreenButton: false,
                targetFrameRate: 10,
                imageryProvider: offline() ? createNaturalEarthProvider() : Imaginary,
            });

            const map_details = JSON.parse(sessionStorage.getItem('map_details') || '{}');
            if (map_details[scenario_id]) {
                if (map_details?.[scenario_id]?.["Eclipse"] && !cesium_viewer.scene.globe.enableLighting) {
                    cesium_viewer.scene.globe.enableLighting = true;
                } else if (!map_details?.[scenario_id]?.["Eclipse"] && cesium_viewer.scene.globe.enableLighting) {
                    cesium_viewer.scene.globe.enableLighting = false;
                }
                if (map_details?.[scenario_id]?.['View'] === '3D') {
                    cesium_viewer.scene.morphTo3D(0)
                    cesium_viewer.scene.screenSpaceCameraController.maximumZoomDistance = maximumZoomDistValue3D;
                } else {
                    cesium_viewer.scene.morphTo2D(0)
                    cesium_viewer.scene.screenSpaceCameraController.inertiaZoom = inertiaZoomValue;
                    cesium_viewer.scene.screenSpaceCameraController.maximumZoomDistance = maximumZoomDistanceValue;
                }
                update_base_map_layer(cesium_viewer, map_details?.[scenario_id]?.["Base Map"]);
            } else {
                cesium_viewer.scene.morphTo2D(2);
                cesium_viewer.scene.screenSpaceCameraController.inertiaZoom = inertiaZoomValue;
                cesium_viewer.scene.screenSpaceCameraController.maximumZoomDistance = maximumZoomDistanceValue;
                const imaginary = new Cesium.MapboxStyleImageryProvider({
                    styleId: 'dark-v10',
                    accessToken: process.env.REACT_APP_MAPBOX_TOKEN!
                });
                cesium_viewer.imageryLayers.addImageryProvider(offline() ? createNaturalEarthProvider() : imaginary);
                const details = {
                    ...map_details,
                    [scenario_id]: {
                        "View": '2D',
                        "Base Map": 'Mapbox Dark',
                        "Eclipse": "Eclipse",
                    }
                }
                sessionStorage.setItem('map_details', JSON.stringify(details));
            }
            cesium_viewer.scene.globe.showGroundAtmosphere = false;
            cesium_viewer.clock.shouldAnimate = true;
            cesium_viewer.clock.clockRange = Cesium.ClockRange.LOOP_STOP;
            cesium_viewer.scene.screenSpaceCameraController.enableRotate = true;
            cesium_viewer.scene.screenSpaceCameraController.enableZoom = true;
            cesium_viewer.scene.screenSpaceCameraController.enableTilt = true;
            cesium_viewer.scene.screenSpaceCameraController.enableLook = true;
            cesium_viewer.scene.highDynamicRange = false;
            cesium_viewer.scene.skyAtmosphere.show = false;
            cesium_viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);


            set_map_viewer(cesium_viewer)
            setTimeout(() => {
                cesium_viewer.scene.camera.flyTo({
                    destination: Cesium.Cartesian3.fromDegrees(0, 0, 35500000),
                });
            }, 1000)
        }

    }, [scenario_id])

    useEffect(() => {
        if (orbitData && map_viewer && sat_grp_id && sat_id) {
            const satelliteGroups: SatelliteGroupOrbitType[] = orbitData?.satellites?.flatMap((satGroup: any) => satGroup.satelliteGroups) || [];
            const selectedSatellite = satelliteGroups?.find(
                sat => sat.satelliteId === sat_id
            );
            if (!selectedSatellite) return;
            const groupsSatellites: VisualizationDataType[] = satelliteGroups.length ? satelliteGroups.flatMap((satGroup: SatelliteGroupOrbitType) => satGroup?.['visualizationData']) : []
            const groundTargets = selectedSatellite?.visualizationData?.['groundTarget'] ? selectedSatellite?.visualizationData?.['groundTarget'] : []
            createPackets(groupsSatellites)
            setGroundTargets(groundTargets)

        }
    }, [orbitData, map_viewer, sat_id])


    // GS hover for simulate/summary mode (CZML 'GS_Object' data source)
    useEffect(() => {
        if (!map_viewer || isOperateMode(dashboard, scenario)) return
        gsHoverHandlerRef.current?.destroy()
        gsHoverHandlerRef.current = setup_gs_hover(map_viewer)
        return () => {
            gsHoverHandlerRef.current?.destroy()
            gsHoverHandlerRef.current = null
        }
    }, [map_viewer, dashboard, scenario])

    // Effect 1: create/recreate GS billboard entities
    useEffect(() => {
        const operateMode = isOperateMode(dashboard, scenario)
        if (configured_gs?.length && map_viewer && operateMode) {
            create_gs_packet(configured_gs, map_viewer, dashboard ?? '', gsStatusList)
        }
    }, [configured_gs, map_viewer, dashboard, scenario, gsStatusList])

    // Effect 2: update ring animations based on active contacts — never recreates entities
    const prevActiveGsIdsRef = useRef<string>('')
    useEffect(() => {
        if (!map_viewer || !isOperateMode(dashboard, scenario) || !configured_gs?.length) return
        // Skip if the set of active GS IDs hasn't changed
        const activeKey = (ongoing_contacts ?? []).map(c => c.groundStationId).sort().join(',')
        if (activeKey === prevActiveGsIdsRef.current) return
        prevActiveGsIdsRef.current = activeKey
        updateGsContactRings(map_viewer, ongoing_contacts ?? [], configured_gs, dashboard ?? '')
    }, [ongoing_contacts, map_viewer])


    const getSatelliteModelUrl = async ( groupId: any,mission_id: any) => {
            try {
                const res = await MissionService.get_satellite_by_sat_id(groupId, mission_id);
                if (res?.data) {
                    const modelRes = await MissionService.getModelUrl(
                        groupId,
                        res.data?.modelNames?.modelName
                    );
                    if (modelRes?.data) {
                        return modelRes.data;
                    }
                }
                return null;
            } catch {
                return null
            }
        };


    const createPackets = async (groupsSatellites: VisualizationDataType[]) => {
        if (!map_viewer || !map_viewer.dataSources) return
        try {
            const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase();
            const useBillboardNotModel = deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER || deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE;
            const modelInfo = useBillboardNotModel
                ? null
                : await getSatelliteModelUrl(selected_satellite_group?.value?.satelliteGroupId, mission_id);
            const response = await createSatellitePackets(map_viewer, groupsSatellites, sat_id, dashboard, modelInfo)  // Create the satellite packet
            if (response) {
                update_viewer(map_viewer)
                map_viewer.scene.requestRender();
            }

            set_viewer(map_viewer)
        } catch {
            // Packet creation error — viewer may be disposed
        } finally {
            setLoading('viewer', false)
        }
    }



    /** In case of Operate, currently different APIs are called to get the satelliteData, ground station data.
     * The data required to create cesium packet is getting in "orbitData", similarly data is obtained in case of Simulation.
     * At present only Satellite data is available in OrbitData response and rest other are empty.
     * In the below useEffect,the same functions which is used in Simulation are being called for packet creation for Operate as well.
     * Uncomment this code, when complete data will be available in OrbitData to create cesium packets.
    */
 
    // useEffect(() => {
    //     if (orbitData && map_viewer && sat_grp_id && sat_id) {
    //         const satelliteGroups: SatelliteGroupOrbitType[] = orbitData?.satellites?.flatMap((satGroup: any) => satGroup.satelliteGroups) || [];
    //         const selectedSatellite = satelliteGroups?.find(sat => sat.satelliteId === sat_id);
    //         if (!selectedSatellite) return;
    //         const document = selectedSatellite?.visualizationData?.documentMetadata
    //         const satellites = selectedSatellite?.visualizationData?.['satellites'] ? selectedSatellite?.visualizationData?.['satellites'] : []
    //         const groundStations = selectedSatellite?.visualizationData?.['groundStation'] ? selectedSatellite?.visualizationData?.['groundStation'] : []
    //         const satToGsConnection = selectedSatellite?.visualizationData?.['satToGsConnection'] ? selectedSatellite?.visualizationData?.['satToGsConnection'] : []

    //         const groundTargets = selectedSatellite?.visualizationData?.['groundTarget'] ? selectedSatellite?.visualizationData?.['groundTarget'] : []
    //         createPacketsNew(document, satellites, groundStations, satToGsConnection)
    //         setGroundTargets(groundTargets)
    //     }
    // }, [orbitData, map_viewer, sat_id])

    // const createPacketsNew = (document, satellites, groundStations, satToGsConnection) => {
    //     if (!map_viewer || !map_viewer.dataSources) return
    //     try{
    //         // clean the previous czml or data source
    //         map_viewer.dataSources.removeAll();
    //         const czmlPackages: any[] = []
    //         let satelliteczml: any[] = [];
    //         let groundStationczml: any[] = [];
    //         let satToGsConnections: any[] = [];
    
    //         satelliteczml = getSatellitePackete(document, satellites, sat_id, dashboard)  // Create the satellite packet
    //         groundStationczml = getGroundStationPacket(satellites, groundStations, true)  // Create the Ground statio packet
    //         satToGsConnections = groundStationToSatelliteConnection(satToGsConnection)
    
    //         if (satelliteczml?.length > 0) {
    //             czmlPackages.push(...satelliteczml)
    //         } if (groundStationczml?.length > 0) {
    //             czmlPackages.push(...groundStationczml)
    //         } if (satToGsConnections?.length > 0) {
    //             czmlPackages.push(...satToGsConnections)
    //         }
            
    //         if (czmlPackages.length > 0) {
    //             map_viewer.dataSources.add(Cesium.CzmlDataSource.load(czmlPackages));
    //             update_viewer(map_viewer)
    //             map_viewer.scene.requestRender();
    //         }
    //         set_viewer(map_viewer)
    //         setLoading('viewer', false)
    //     } catch (error) {
    //         console.log(error)
    //     } finally {
    //         setLoading('viewer', false)
    //     }
    // }

    return {
        map_viewer,
        cesiumContainerRef,
        groundTargets
    }
}

export default useCesium